import httpClient from './httpClient';
import { API_CONFIG } from '../utils/constants';

// ============================================================================
// Data Models - matching the API contract
// ============================================================================

export interface Problem {
  id: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  language: string;
  category: string;
  test_case_count: number;
}

export interface Example {
  input: string;
  output: string;
  explanation?: string;
}

export interface ProblemDetail {
  id: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  language: string;
  category: string;
  description: string;
  input_format: string;
  output_format: string;
  constraints: string[];
  examples: Example[];
  starter_code: string;
  time_limit_seconds: number;
  memory_limit_mb: number;
  test_case_count: number;
}

export interface TestResult {
  test_case: number;
  passed: boolean;
  time_ms: number;
  expected?: string;
  actual?: string;
  error?: string;
}

export type EvaluationStatus = 
  | 'accepted' 
  | 'partial' 
  | 'wrong_answer' 
  | 'time_limit' 
  | 'runtime_error' 
  | 'compile_error';

export interface EvaluationSummary {
  status: EvaluationStatus;
  passed: number;
  total: number;
  score: number;
  total_time_ms: number;
}

export interface ProblemsResponse {
  success: boolean;
  page: number;
  limit: number;
  total_problems: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  problems: Problem[];
}

export interface ProblemStatsResponse {
  success: boolean;
  total_problems: number;
  languages: string[];
  categories: string[];
  difficulty_distribution: {
    easy: number;
    medium: number;
    hard: number;
  };
  version: string;
}

export interface ProblemsFilter {
  page?: number;
  limit?: number;
  language?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  category?: string;
  shuffle?: boolean;
}

export interface EvaluateCodeRequest {
  problem_id: string;
  language: string;
  code: string;
}

// Simple code execution request (for Run with custom input)
export interface ExecuteCodeRequest {
  language: string;
  version?: string;
  code: string;
  stdin?: string;
}

// Default versions for common languages (Piston requires version)
const DEFAULT_VERSIONS: Record<string, string> = {
  python: '3.12.0',
  javascript: '20.11.1',
  java: '15.0.2',
  cpp: '10.2.0',
  'c++': '10.2.0',
  c: '10.2.0',
  typescript: '5.0.3',
  go: '1.16.2',
  rust: '1.68.2',
  ruby: '3.0.1',
  php: '8.2.3',
  kotlin: '1.8.20',
  'csharp.net': '5.0.201',
  'fsharp.net': '5.0.201',
  'basic.net': '5.0.201',
  fsi: '5.0.201',
  sqlite3: '3.36.0',
  rscript: '4.1.1',
  fortran: '10.2.0',
  d: '10.2.0',
};

// Simple code execution response
export interface ExecuteCodeResponse {
  language: string;
  output: string;
}

// SSE Event callbacks
export interface EvaluationCallbacks {
  onTestResult: (result: TestResult) => void;
  onComplete: (summary: EvaluationSummary) => void;
  onError: (error: string) => void;
}

// ============================================================================
// Activity Tracking Types
// ============================================================================

export interface PasteEvent {
  timestamp: number;
  charCount: number;
  codeLength: number;
}

export interface KPMSample {
  timestamp: number;
  kpm: number;
}

export interface CodeLengthSample {
  timestamp: number;
  length: number;
}

export interface IdlePeriod {
  startTime: number;
  endTime: number;
  durationSeconds: number;
}

export interface FocusEvent {
  timestamp: number;
  type: 'blur' | 'focus';
}

export interface ActivityMetrics {
  totalKeystrokes: number;
  averageKPM: number;
  kpmHistory: KPMSample[];
  pasteEvents: PasteEvent[];
  totalPastedChars: number;
  idlePeriods: IdlePeriod[];
  totalIdleSeconds: number;
  codeLengthTimeline: CodeLengthSample[];
  focusEvents: FocusEvent[];
  totalBlurTimeSeconds: number;  // Total time tab was not focused (seconds)
  totalCodeTimeSeconds: number;  // Total time spent on coding session (seconds)
}

// ============================================================================
// Session Management Types
// ============================================================================

export interface CodeRun {
  code: string;
  testResults: TestResult[];
  summary: EvaluationSummary | null;
  timestamp: number;
  runNumber: number;
}

export interface StartSessionRequest {
  problem_id: string;
  language: string;
  mode: 'timed' | 'practice';
  timed_duration_seconds?: number;
}

export interface StartSessionResponse {
  success: boolean;
  session_id: string;
}

export interface SubmitSessionRequest {
  session_id: string;
  problem_id: string;
  language: string;
  mode: 'timed' | 'practice';
  start_time: number;
  end_time: number;
  final_code: string;
  attempt_count: number;
  final_score: number;
  final_status: EvaluationStatus;
  run_history: CodeRun[];
  activity_metrics?: ActivityMetrics;
}

export interface AbandonSessionRequest {
  session_id: string;
  last_code: string;
  time_spent_seconds: number;
  reason: 'tab_closed' | 'ended_early' | 'timer_expired_no_submit';
}

// ============================================================================
// Code Hints Types (Practice Mode)
// ============================================================================

export interface HintChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface HintChatRequest {
  problem_id?: string;
  problem_statement: string;
  language: string;
  user_code: string;
  user_message: string;
  history?: HintChatMessage[];
}

export interface HintChatResponse {
  response: string;
}

export interface HintMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  codeSnapshot?: string;
  codeLength?: number;
}

// ============================================================================
// API Client
// ============================================================================

/**
 * Get auth token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const codeEvaluationApi = {
  /**
   * Fetch paginated list of problems
   * GET /api/problems
   */
  getProblems: async (filters: ProblemsFilter = {}): Promise<ProblemsResponse> => {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.language) params.append('language', filters.language);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.category) params.append('category', filters.category);
    if (filters.shuffle !== undefined) params.append('shuffle', filters.shuffle.toString());

    const queryString = params.toString();
    const url = queryString ? `problems?${queryString}` : 'problems';
    
    const res = await httpClient.get(url);
    return res.data as ProblemsResponse;
  },

  /**
   * Fetch full problem details including starter code
   * GET /api/problems/{language}/{problem_id}
   */
  getProblemDetails: async (language: string, problemId: string): Promise<ProblemDetail> => {
    const res = await httpClient.get(`problems/${language}/${problemId}`);
    return res.data as ProblemDetail;
  },

  /**
   * Get problem bank statistics
   * GET /api/problems/stats
   */
  getStats: async (): Promise<ProblemStatsResponse> => {
    const res = await httpClient.get('problems/stats');
    return res.data as ProblemStatsResponse;
  },

  /**
   * Execute code with custom input (for Run button)
   * POST /api/code_execution
   */
  executeCode: async (request: ExecuteCodeRequest): Promise<ExecuteCodeResponse> => {
    // Ensure version is provided (Piston requires it)
    const payload = {
      ...request,
      version: request.version || DEFAULT_VERSIONS[request.language] || '1.0.0',
    };
    const res = await httpClient.post('code_execution', payload);
    return res.data as ExecuteCodeResponse;
  },

  /**
   * Evaluate code with SSE streaming for real-time test results
   * POST /api/evaluate/stream
   * 
   * Uses fetch API with ReadableStream for SSE since EventSource doesn't support POST
   */
  evaluateCode: async (
    request: EvaluateCodeRequest,
    callbacks: EvaluationCallbacks
  ): Promise<void> => {
    const token = getAuthToken();
    
    if (!token) {
      callbacks.onError('Authentication required. Please log in.');
      return;
    }

    const url = `${API_CONFIG.BASE_URL}evaluate/stream`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        callbacks.onError(errorData.detail || `HTTP ${response.status}`);
        return;
      }

      if (!response.body) {
        callbacks.onError('No response body received');
        return;
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line === '' && currentEvent && currentData) {
            // Empty line marks end of event
            try {
              const parsedData = JSON.parse(currentData);
              
              switch (currentEvent) {
                case 'test_result':
                  callbacks.onTestResult(parsedData as TestResult);
                  break;
                case 'complete':
                  callbacks.onComplete(parsedData as EvaluationSummary);
                  break;
                case 'error':
                  callbacks.onError(parsedData.error || 'Evaluation error');
                  break;
                default:
                  console.warn('Unknown SSE event:', currentEvent);
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', currentData, parseError);
            }
            
            // Reset for next event
            currentEvent = '';
            currentData = '';
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        callbacks.onError('Evaluation cancelled');
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('Network')) {
        callbacks.onError('Network error. Please check your connection.');
      } else {
        callbacks.onError(error.message || 'Failed to evaluate code');
      }
    }
  },

  /**
   * Create an AbortController for cancelling SSE stream
   */
  createAbortController: (): AbortController => {
    return new AbortController();
  },

  // ==========================================================================
  // Session Management APIs
  // ==========================================================================

  /**
   * Start a coding interview session
   * POST /api/coding/start
   */
  startSession: async (request: StartSessionRequest): Promise<StartSessionResponse> => {
    const res = await httpClient.post('coding/start', request);
    return res.data as StartSessionResponse;
  },

  /**
   * Submit completed coding interview
   * POST /api/coding/submit
   */
  submitSession: async (request: SubmitSessionRequest): Promise<void> => {
    console.log('API: Submitting session to /api/coding/submit', request);
    const response = await httpClient.post('coding/submit', request);
    console.log('API: Submit response', response);
    return response.data;
  },

  /**
   * Abandon session (user left early, tab closed, etc.)
   * POST /api/coding/abandon
   */
  abandonSession: async (request: AbandonSessionRequest): Promise<void> => {
    try {
      await httpClient.post('coding/abandon', request);
    } catch (err) {
      console.warn('Failed to abandon coding session:', err);
    }
  },

  // ==========================================================================
  // Code Hints APIs (Practice Mode)
  // ==========================================================================

  /**
   * Send a hint chat message
   * POST /api/coding/hints/chat
   * 
   * First call: Include problem_statement to create cache
   * Subsequent calls: Include cache_id to reuse cached context
   */
  sendHintChat: async (request: HintChatRequest): Promise<HintChatResponse> => {
    const res = await httpClient.post('coding/hints/chat', request);
    return res.data as HintChatResponse;
  },
};

export default codeEvaluationApi;

